import React from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import actionCreators from "@modules/reporting/actionCreator";

import { Button, GSGrid, Modal, SimpleGrid, Text, View } from "@components";
import { route } from "@routes/helper";
import type { AbsenceRateTrendBreakdownData } from "@modules/reporting/reducer/trends/absence-rate/types";

type OwnProps = {
  data: AbsenceRateTrendBreakdownData,
  clientId: number,
  t: Function,
};

type PropsFromDispatch = {
  resetComplianceTrendBreakdown: typeof actionCreators.ResetComplianceTrendsBreakdown,
  exportBreakdown: typeof actionCreators.ExportComplianceTrendsBreakdown,
};

type Props = {
  ...OwnProps,
  ...PropsFromLocalize,
  ...PropsFromDispatch,
};

export function ComplianceTrendsBreakdownModal(props: Props) {
  const handleCloseModal = () => () => {
    props.resetComplianceTrendBreakdown();
  };

  function renderBreakdownDetails() {
    const { details, filters } = props.data;

    return (
      <SimpleGrid
        className="theme-breakdown"
        columns={[
          {
            name: "label",
          },
          {
            name: "details",
          },
        ]}
        rows={[
          {
            id: "department",
            label:
              filters.groupBy === "ORGANISATIONAL_UNIT"
                ? props.t("DEPARTMENT")
                : props.t(filters.groupBy),
            details: details.name,
          },
          {
            id: "date-start",
            label: props.t("PERIOD_START"),
            details: details.fromDate,
          },
          {
            id: "from-date",
            label: props.t("PERIOD_END"),
            details: details.toDate,
          },
          {
            id: "completed",
            label: props.t("COMPLETED_RTW"),
            details: details.completed,
          },
          {
            id: "expired",
            label: props.t("EXPIRED_RTW"),
            details: details.expired,
          },
          {
            id: "total",
            label: props.t("TOTAL_RTW"),
            details: details.total,
          },
          {
            id: "compliant",
            label: props.t("COMPLIANT_RTW"),
            details: details.compliant,
          },
          {
            id: "value",
            label: props.t("RTW_COMPLIANCE"),
            details: `${details.value}%`,
          },
        ]}
      />
    );
  }

  function renderBreakdownTable() {
    const { list } = props.data;
    const columns = [
      {
        name: props.t("EMPLOYEE"),
        key: "employeeName",
        align: "left",
      },
      {
        name: props.t("ABSENCE_ID"),
        key: "absenceId",
        align: "center",
      },
      {
        name: props.t("STATUS"),
        key: "status",
        align: "left",
        // renderCell: ({ row }: Object) =>
        //   props.t(row.status === "Late" ? "COMPLETED_LATE" : row.status.toUpperCase()),
      },
      {
        name: props.t("VIEW"),
        key: "action",
        align: "center",
        renderCell: ({ row }: Object) => (
          <a
            href={route("REPORTING_EMPLOYEE_ABSENCE_DETAILS_PATH", {
              clientId: props.clientId,
              employeeId: row.employeeId,
              absenceId: row.absenceId,
            })}
            target="_blank"
            rel="noreferrer"
            className="d-flex pointer text-decoration-none"
          >
            <span className="material-icons font-size-30 pointer">play_circle</span>
          </a>
        ),
      },
    ];

    return <GSGrid rows={list} columns={columns} maxHeight={"58vh"} className="breakdown-table" />;
  }

  function exportBreakdownTable() {
    props.exportBreakdown({
      clientId: props.clientId,
      filters: props.data.filters,
      details: props.data.details,
    });
  }

  function exportBreakdownButton() {
    return (
      props.data.list.length > 0 && (
        <Button materialIcon="download" onClick={exportBreakdownTable}>
          {props.t("EXPORT")}
        </Button>
      )
    );
  }

  return (
    <Modal onToggle={handleCloseModal()} rightPanel width="30vw">
      <View flex={1} padding={[4, 6, 6, 6]} gap={4}>
        {/* Modal header */}
        <View
          direction="row"
          separator="bottom"
          backgroundColor={"white"}
          position="sticky"
          zIndex={5}
          top={0}
          justifyContent="space-between"
          alignItems="center"
        >
          <h5>{props.t("RTW_INTERVIEW_BREAKDOWN")}</h5>
          <Button onClick={handleCloseModal()} color="secondary" label={props.t("CLOSE")} />
        </View>

        <View flex={1}>{renderBreakdownDetails()}</View>

        <View flex={1} gap={3}>
          <View direction="row" justifyContent="space-between" alignItems="center">
            <Text type="h6" text={props.t("NON_COMPLIANT_INTERVIEWS")} />
            {exportBreakdownButton()}
          </View>

          {renderBreakdownTable()}
        </View>
      </View>
    </Modal>
  );
}

const mapDispatchToProps: PropsFromDispatch = {
  resetComplianceTrendBreakdown: actionCreators.ResetComplianceTrendsBreakdown,
  exportBreakdown: actionCreators.ExportComplianceTrendsBreakdown,
};

export default compose(
  connect(null, mapDispatchToProps),
  localize(),
)(ComplianceTrendsBreakdownModal);
